import { useState, useCallback, useEffect } from 'react'
import {
  Table,
  TextInput,
  Select,
  Group,
  Paper,
  Text,
  Badge,
  ActionIcon,
  Pagination,
  Button,
  Affix,
  Transition,
  LoadingOverlay,
  Tooltip,
  Switch,
  SimpleGrid,
  ThemeIcon,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { useDisclosure } from '@mantine/hooks'
import {
  IconSearch,
  IconSortAscending,
  IconSortDescending,
  IconFilter,
  IconFilterOff,
  IconDeviceFloppy,
  IconX,
  IconAlertTriangle,
  IconCheck,
  IconDatabase,
  IconClock,
  IconShieldCheck,
} from '@tabler/icons-react'
import { useSourceLabels, useSourceLabelBatchUpdate } from '@/hooks/useLabeling'
import {
  SOURCE_CATEGORIES,
  type SourceLabelQuery,
  type SourceLabelRow,
  type LabelStatus,
} from '@/services/labeling.api'
import { StatusBadge } from './ChargeLabelTable'

interface PendingEdit {
  human_label: string | null
  label_status: LabelStatus
}

export function SourceLabelTable() {
  const [query, setQuery] = useState<SourceLabelQuery>({
    page: 1,
    pageSize: 50,
    sortBy: 'source_raw',
    sortOrder: 'asc',
  })

  const [searchInput, setSearchInput] = useState('')
  const [showFilters, { toggle: toggleFilters }] = useDisclosure(true)
  const [pendingEdits, setPendingEdits] = useState<Map<string, PendingEdit>>(new Map())

  const { data, isLoading, isFetching } = useSourceLabels(query)
  const batchUpdate = useSourceLabelBatchUpdate()

  const rows = data?.rows ?? []
  const total = data?.total ?? 0
  const stats = data?.stats
  const totalPages = Math.ceil(total / query.pageSize)
  const hasPendingEdits = pendingEdits.size > 0

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (pendingEdits.size > 0) e.preventDefault()
    }
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [pendingEdits.size])

  const confirmDiscard = useCallback(() => {
    if (!hasPendingEdits) return true
    if (window.confirm(`You have ${pendingEdits.size} unsaved changes. Discard them?`)) {
      setPendingEdits(new Map())
      return true
    }
    return false
  }, [hasPendingEdits, pendingEdits.size])

  const handleSearch = useCallback(() => {
    if (!confirmDiscard()) return
    setQuery(q => ({ ...q, page: 1, search: searchInput || undefined }))
  }, [searchInput, confirmDiscard])

  const handleSort = useCallback((column: string) => {
    setQuery(q => ({
      ...q,
      sortBy: column,
      sortOrder: q.sortBy === column && q.sortOrder === 'asc' ? 'desc' : 'asc',
    }))
  }, [])

  const handlePageChange = useCallback((page: number) => {
    if (!confirmDiscard()) return
    setQuery(q => ({ ...q, page }))
  }, [confirmDiscard])

  const handleFilterChange = useCallback((partial: Partial<SourceLabelQuery>) => {
    if (!confirmDiscard()) return
    setQuery(q => ({ ...q, ...partial, page: 1 }))
  }, [confirmDiscard])

  const handleClearFilters = useCallback(() => {
    setSearchInput('')
    setQuery({ page: 1, pageSize: query.pageSize, sortBy: 'source_raw', sortOrder: 'asc' })
  }, [query.pageSize])

  const getDisplayLabel = useCallback((row: SourceLabelRow): string | null => {
    const edit = pendingEdits.get(row.id)
    if (edit) return edit.human_label
    return row.human_label
  }, [pendingEdits])

  const getDisplayStatus = useCallback((row: SourceLabelRow): LabelStatus => {
    return pendingEdits.get(row.id)?.label_status ?? row.label_status
  }, [pendingEdits])

  const setEdit = useCallback((row: SourceLabelRow, edit: PendingEdit | null) => {
    setPendingEdits(prev => {
      const next = new Map(prev)
      if (
        edit &&
        edit.human_label === row.human_label &&
        edit.label_status === row.label_status
      ) {
        next.delete(row.id)
      } else if (edit) {
        next.set(row.id, edit)
      } else {
        next.delete(row.id)
      }
      return next
    })
  }, [])

  const handleSelectChange = useCallback((row: SourceLabelRow, value: string | null) => {
    if (!value) return setEdit(row, null)
    const status: LabelStatus = row.human_label ? 'RELABELED' : 'LABELED'
    setEdit(row, { human_label: value, label_status: status })
  }, [setEdit])

  const handleConfirm = useCallback((row: SourceLabelRow) => {
    const current = getDisplayStatus(row)
    const label = getDisplayLabel(row)
    if (current === 'CONFIRMED') {
      setEdit(row, null)
    } else if (label) {
      setEdit(row, { human_label: label, label_status: 'CONFIRMED' })
    } else {
      notifications.show({
        title: 'Pick a category first',
        message: 'Choose a source category before confirming.',
        color: 'yellow',
      })
    }
  }, [getDisplayStatus, getDisplayLabel, setEdit])

  const handleReject = useCallback((row: SourceLabelRow) => {
    const current = getDisplayStatus(row)
    if (current === 'REJECTED') {
      setEdit(row, null)
    } else {
      setEdit(row, { human_label: null, label_status: 'REJECTED' })
    }
  }, [getDisplayStatus, setEdit])

  const handleSaveAll = useCallback(async () => {
    const updates = Array.from(pendingEdits.entries()).map(([id, edit]) => ({
      id,
      human_label: edit.human_label,
      label_status: edit.label_status,
    }))
    try {
      const result = await batchUpdate.mutateAsync(updates)
      if (result.totalErrors > 0) {
        const remaining = new Map<string, PendingEdit>()
        for (const id of result.errorIds) {
          const edit = pendingEdits.get(id)
          if (edit) remaining.set(id, edit)
        }
        setPendingEdits(remaining)
        notifications.show({
          title: 'Partial save',
          message: `${result.totalUpdated} saved, ${result.totalErrors} errors`,
          color: 'yellow',
          icon: <IconAlertTriangle size={16} />,
        })
      } else {
        setPendingEdits(new Map())
        notifications.show({
          title: 'Labels saved',
          message: `${result.totalUpdated} source${result.totalUpdated !== 1 ? 's' : ''} updated`,
          color: 'green',
          icon: <IconCheck size={16} />,
        })
      }
    } catch {
      notifications.show({
        title: 'Save failed',
        message: 'Could not save changes. Please try again.',
        color: 'red',
        icon: <IconX size={16} />,
      })
    }
  }, [pendingEdits, batchUpdate])

  const SortIcon = ({ column }: { column: string }) => {
    if (query.sortBy !== column) return null
    return query.sortOrder === 'asc'
      ? <IconSortAscending size={14} />
      : <IconSortDescending size={14} />
  }

  const sortableHeader = (label: string, column: string) => (
    <Table.Th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort(column)}>
      <Group gap={4} wrap="nowrap">
        <Text size="xs" fw={600}>{label}</Text>
        <SortIcon column={column} />
      </Group>
    </Table.Th>
  )

  return (
    <div style={{ position: 'relative' }}>
      <LoadingOverlay visible={isLoading && !data} />

      {stats && (
        <SimpleGrid cols={{ base: 2, sm: 5 }} mb="md">
          <Paper p="md" radius="md" withBorder>
            <Group gap="xs" mb={4}>
              <ThemeIcon size="sm" variant="light" color="blue"><IconDatabase size={14} /></ThemeIcon>
              <Text size="xs" c="dimmed" fw={500}>Total Sources</Text>
            </Group>
            <Text size="xl" fw={700}>{stats.totalRows.toLocaleString()}</Text>
          </Paper>
          <Paper p="md" radius="md" withBorder>
            <Group gap="xs" mb={4}>
              <ThemeIcon size="sm" variant="light" color="gray"><IconClock size={14} /></ThemeIcon>
              <Text size="xs" c="dimmed" fw={500}>Pending</Text>
            </Group>
            <Text size="xl" fw={700}>{stats.pendingCount.toLocaleString()}</Text>
          </Paper>
          <Paper p="md" radius="md" withBorder>
            <Group gap="xs" mb={4}>
              <ThemeIcon size="sm" variant="light" color="green"><IconCheck size={14} /></ThemeIcon>
              <Text size="xs" c="dimmed" fw={500}>Confirmed</Text>
            </Group>
            <Text size="xl" fw={700} c="green">{stats.confirmedCount.toLocaleString()}</Text>
          </Paper>
          <Paper p="md" radius="md" withBorder>
            <Group gap="xs" mb={4}>
              <ThemeIcon size="sm" variant="light" color="red"><IconX size={14} /></ThemeIcon>
              <Text size="xs" c="dimmed" fw={500}>Rejected</Text>
            </Group>
            <Text size="xl" fw={700} c="red">{stats.rejectedCount.toLocaleString()}</Text>
          </Paper>
          <Paper p="md" radius="md" withBorder>
            <Group gap="xs" mb={4}>
              <ThemeIcon size="sm" variant="light" color="teal"><IconShieldCheck size={14} /></ThemeIcon>
              <Text size="xs" c="dimmed" fw={500}>Ground Truth</Text>
            </Group>
            <Text size="xl" fw={700} c="teal">{stats.groundTruthCount.toLocaleString()}</Text>
          </Paper>
        </SimpleGrid>
      )}

      <Paper p="sm" radius="md" withBorder mb="md">
        <Group justify="space-between" mb={showFilters ? 'sm' : 0}>
          <Group gap="sm">
            <TextInput
              placeholder="Search raw sources..."
              leftSection={<IconSearch size={16} />}
              value={searchInput}
              onChange={(e) => setSearchInput(e.currentTarget.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              size="sm"
              style={{ minWidth: 280 }}
            />
            <Button size="sm" variant="light" onClick={handleSearch}>Search</Button>
          </Group>
          <Group gap="sm">
            <ActionIcon variant="subtle" onClick={toggleFilters} title="Toggle filters">
              {showFilters ? <IconFilterOff size={18} /> : <IconFilter size={18} />}
            </ActionIcon>
            <Button size="xs" variant="subtle" color="gray" onClick={handleClearFilters}>Clear All</Button>
          </Group>
        </Group>

        {showFilters && (
          <Group gap="md" align="flex-end">
            <Select
              label="Category"
              placeholder="All"
              size="xs"
              clearable
              data={SOURCE_CATEGORIES as unknown as string[]}
              value={query.humanLabel ?? null}
              onChange={(val) => handleFilterChange({ humanLabel: val || undefined })}
              style={{ minWidth: 200 }}
            />
            <Select
              label="Status"
              placeholder="All"
              size="xs"
              clearable
              data={['PENDING', 'LABELED', 'CONFIRMED', 'REJECTED', 'RELABELED']}
              value={query.labelStatus ?? null}
              onChange={(val) => handleFilterChange({ labelStatus: val || undefined })}
              style={{ minWidth: 140 }}
            />
            <Switch
              label="Ground Truth only"
              size="xs"
              checked={query.isGroundTruth === true}
              onChange={(e) => handleFilterChange({ isGroundTruth: e.currentTarget.checked ? true : undefined })}
            />
          </Group>
        )}
      </Paper>

      <Group justify="space-between" mb="xs">
        <Text size="sm" c="dimmed">
          {isFetching ? 'Loading...' : `${total.toLocaleString()} results`}
          {query.page > 1 && ` (page ${query.page})`}
        </Text>
        <Select
          size="xs"
          value={String(query.pageSize)}
          onChange={(val) => handleFilterChange({ pageSize: parseInt(val!) })}
          data={['25', '50', '100']}
          style={{ width: 80 }}
        />
      </Group>

      <Paper withBorder radius="md" style={{ overflow: 'auto' }}>
        <Table striped highlightOnHover withColumnBorders>
          <Table.Thead>
            <Table.Tr>
              {sortableHeader('Raw Source', 'source_raw')}
              <Table.Th style={{ minWidth: 240 }}><Text size="xs" fw={600}>Normalized Category</Text></Table.Th>
              <Table.Th><Text size="xs" fw={600}>Status</Text></Table.Th>
              <Table.Th style={{ width: 90 }}><Text size="xs" fw={600}>Actions</Text></Table.Th>
              {sortableHeader('Labeled By', 'labeled_at')}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {rows.length === 0 && !isLoading && (
              <Table.Tr>
                <Table.Td colSpan={5}>
                  <Text ta="center" c="dimmed" py="xl">No records found</Text>
                </Table.Td>
              </Table.Tr>
            )}
            {rows.map((row) => {
              const edit = pendingEdits.get(row.id)
              const isEdited = !!edit
              const displayStatus = getDisplayStatus(row)
              const isRejected = displayStatus === 'REJECTED'

              return (
                <Table.Tr
                  key={row.id}
                  style={{
                    borderLeft: isEdited ? '3px solid var(--mantine-color-blue-5)' : undefined,
                    backgroundColor: isEdited ? 'var(--mantine-color-blue-0)' : undefined,
                  }}
                >
                  <Table.Td style={{ maxWidth: 360 }}>
                    <Tooltip label={row.source_raw} multiline maw={400}>
                      <Text size="sm" lineClamp={2}>{row.source_raw}</Text>
                    </Tooltip>
                  </Table.Td>

                  <Table.Td>
                    <Select
                      size="xs"
                      placeholder="Pick category"
                      data={SOURCE_CATEGORIES as unknown as string[]}
                      value={isRejected ? null : getDisplayLabel(row)}
                      disabled={isRejected}
                      onChange={(val) => handleSelectChange(row, val)}
                      comboboxProps={{ withinPortal: true }}
                    />
                  </Table.Td>

                  <Table.Td><StatusBadge status={displayStatus} /></Table.Td>

                  <Table.Td>
                    <Group gap={4} wrap="nowrap">
                      <Tooltip label={displayStatus === 'CONFIRMED' ? 'Undo confirm' : 'Confirm label'}>
                        <ActionIcon
                          size="sm"
                          variant={displayStatus === 'CONFIRMED' ? 'filled' : 'subtle'}
                          color="green"
                          onClick={() => handleConfirm(row)}
                        >
                          <IconCheck size={14} />
                        </ActionIcon>
                      </Tooltip>
                      <Tooltip label={isRejected ? 'Undo reject' : 'Reject (unusable)'}>
                        <ActionIcon
                          size="sm"
                          variant={isRejected ? 'filled' : 'subtle'}
                          color="red"
                          onClick={() => handleReject(row)}
                        >
                          <IconX size={14} />
                        </ActionIcon>
                      </Tooltip>
                    </Group>
                  </Table.Td>

                  <Table.Td>
                    {row.labeled_by
                      ? (
                        <div>
                          <Text size="xs">{row.labeled_by}</Text>
                          {row.labeled_at && (
                            <Text size="xs" c="dimmed">{new Date(row.labeled_at).toLocaleDateString()}</Text>
                          )}
                        </div>
                      )
                      : <Text size="xs" c="dimmed">-</Text>}
                  </Table.Td>
                </Table.Tr>
              )
            })}
          </Table.Tbody>
        </Table>
      </Paper>

      {totalPages > 1 && (
        <Group justify="center" mt="md">
          <Pagination total={totalPages} value={query.page} onChange={handlePageChange} size="sm" />
        </Group>
      )}

      <Affix position={{ bottom: 20, left: '50%' }} style={{ transform: 'translateX(-50%)' }}>
        <Transition mounted={hasPendingEdits} transition="slide-up">
          {(styles) => (
            <Paper style={styles} shadow="xl" radius="lg" p="sm" px="lg" withBorder>
              <Group gap="md">
                <Badge size="lg" variant="filled" color="blue" circle>{pendingEdits.size}</Badge>
                <Text size="sm" fw={500}>unsaved change{pendingEdits.size !== 1 ? 's' : ''}</Text>
                <Button
                  size="sm"
                  leftSection={<IconDeviceFloppy size={16} />}
                  onClick={handleSaveAll}
                  loading={batchUpdate.isPending}
                >
                  Save All
                </Button>
                <Button
                  size="sm"
                  variant="subtle"
                  color="gray"
                  leftSection={<IconX size={16} />}
                  onClick={() => setPendingEdits(new Map())}
                  disabled={batchUpdate.isPending}
                >
                  Discard
                </Button>
              </Group>
            </Paper>
          )}
        </Transition>
      </Affix>
    </div>
  )
}
